import { useQueryClient } from "@tanstack/react-query";

import { $api, type components, useIdempotencyKey } from "@/shared/api";

import { DRILL_SERIES_QUERY_KEY, DRILLS_OVERVIEW_QUERY_KEY } from "./DRILLS_QUERY_KEYS";

type DrillAnswer = components["schemas"]["DrillAnswer"];
type DrillSeriesResponse = components["schemas"]["DrillSeriesResponse"];

// Ответ на задачу (`POST /api/drills/{id}/answer`). Ключ идемпотентности — один на задачу,
// пока сервер не ответил окончательно: повтор после обрыва сети вернёт ту же оценку.
// Оценка сразу кладётся в серию в кэше — экран показывает разбор ответа без перезапроса;
// сводка (значок меню, темы) перечитывается.
export const useAnswerDrill = () => {
  const idempotency = useIdempotencyKey();
  const queryClient = useQueryClient();
  const mutation = $api.useMutation("post", "/api/drills/{drill_id}/answer", {
    onSettled: (_data, error) => {
      idempotency.settle(error);
    },
    onSuccess: async (attempt) => {
      queryClient.setQueryData<DrillSeriesResponse>(DRILL_SERIES_QUERY_KEY, (current) =>
        current?.series == null
          ? current
          : {
              series: {
                ...current.series,
                items: current.series.items.map((item) =>
                  item.drill_id === attempt.drill_id ? { ...item, attempt } : item,
                ),
              },
            },
      );
      await queryClient.invalidateQueries({ queryKey: DRILLS_OVERVIEW_QUERY_KEY });
    },
  });
  const answer = (drillId: number, body: DrillAnswer) => {
    mutation.mutate({
      body,
      params: {
        path: { drill_id: drillId },
        header: { "Idempotency-Key": idempotency.keyFor(`drill:${drillId}`) },
      },
    });
  };
  return { ...mutation, answer };
};
